import React, { useState } from 'react';
import styled from 'styled-components';
import { fetchStorageAccountToken } from '@services/azure-upload';
import { isStorageConfigured, uploadFileToBlob } from '@utils/azureUpload';
import { formatFirmNameForEndpoints } from '@utils/generateAssetLinks';

const FlexColumn = styled.div`
    display: flex;
    flex-direction: column;
`;

const StackText = styled(FlexColumn)`
    input, button {
        width: 250px;
    }
`;

const Style = styled(FlexColumn)`
    >div {
        margin: 15px 0;
    }
`;

const UploadFormStyle = styled(FlexColumn)`
    align-items: center;
    >div {
        display: flex;
        justify-content: space-evenly;
        margin: 15px 0;
    }
`;

const Form = styled.form`
    * {
        margin: 5px 0;
    }
`;

const FileTypeButton = styled.button`
    background: ${props => props.selected ? props.theme.secondaryLightBlue : props.theme.lightGrey};
`;

const UploadButton = styled.button`
    color: #fff;
    background-color: #007bff;
    border-color: #007bff;
    font-weight: 400;
    line-height: 1.5;
    border-radius: .25rem;
    padding: .375rem .75rem;
    font-size: 1rem;
`;

export const UploadTab = ({ setBlobUrl, blobUrl }) => {
    const [fileType, setFileType] = useState('Document');
    const [firmName, setFirmName] = useState('');
    const [folderName, setFolderName] = useState('logos');
    const [containerName, setContainerName] = useState('tp-legal-documents');
    const [uploaded, setUploaded] = useState(false);
    const [fileNameReminder, setFileNameReminder] = useState('');
    const [message, setMessage] = useState(`${containerName}/`);
    const [info, setInfo] = useState('Please verify file name formatting before uploading. If applicable, type in the firm and name your file attachment as indicated below the input box. Logos must be svg, backgrounds must be png, and documents must be pdf. Examples: Royal_logo.svg, Great-Lakes_document.pdf, Timberline_background.png.');
    const [selectedFile, setSelectedFile] = useState({ name: '' });
    const [uploading, setUploading] = useState(false);
    const [inputKey, setInputKey] = useState(Math.random().toString(36));
    const [storageConfigured, setStorageConfigured] = useState(true);
    
    const handleTabChange = (event) => {
        event.preventDefault();

        const tab = event.target.value;
        const containerName = tab === 'Document' ? 'tp-legal-documents' : 'tp-images';

        setFileType(tab);
        setContainerName(containerName);
        setMessage(`${containerName}/`);
    };

    const handleFirmName = (event) => {
        const { value } = event.target;

        setFirmName(value);

        const formattedName = formatFirmNameForEndpoints(value);

        let fileAppendix = '';
        let fileEnding = '';
        
        if (fileType === 'Document') {
            fileAppendix = fileType.toLowerCase();
            fileEnding = 'pdf';
        } else {
            fileAppendix = folderName.slice(0, folderName.length - 1);
            fileEnding = folderName === 'logos' ? 'svg' : 'png';
        }

        if (fileType === 'Document' || folderName !== 'slides') {
            setFileNameReminder(`Please (re)name your file to: ${formattedName}_${fileAppendix}.${fileEnding}`);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];

        setSelectedFile(file);

        const message = fileType === 'Document' ? `${containerName}/${file.name}` : `${containerName}/${folderName}/${file.name}`;
        setMessage(message);

        const pdfMismatch = file.type === 'application/pdf' && fileType !== 'Document';
        const imageMismatch = (file.type === 'image/png' || file.type === 'image/svg') && fileType !== 'Image';

        if (pdfMismatch || imageMismatch) {
            setInfo('The file extension and folder type don\'t match. You may be uploading an image to the legal documents folder (or vice versa). Make sure the correct document type is selected.');
        } else {
            setInfo('The file extension and folder type match');
        }
    };

    const handleFolderName = (event) => {
        event.preventDefault();

        const { value } = event.target;

        setFolderName(value);
        setMessage(`${containerName}/${value}/${selectedFile.name}`);

        if (value === 'slides') {
            setFileNameReminder('');
        }
    };

    const handleUpload = async (event) => {
        if (!selectedFile.name) {
            setMessage('Please select a file to upload');
            event.preventDefault();
            return;
        }

        setUploading(true);

        try {
            const { sasToken, storageAccountName } = await fetchStorageAccountToken(fileType);
 
            setStorageConfigured(isStorageConfigured(sasToken, storageAccountName));

            const filePath = `${folderName}/${selectedFile.name}`;
            const { blobUrl } = await uploadFileToBlob(storageAccountName, containerName, sasToken, filePath, selectedFile);
          
            setBlobUrl(blobUrl);
            setMessage(`Successfully posted ${fileType} to blob storage.`);
            setUploaded(true);
            setInfo(`View your ${fileType} here: ${blobUrl}`);
        } catch (err) {
            // Add more specific error handling for overwrites etc.
            console.error(err);
            setMessage(`Error posting file to blob storage: ${err.message}`);
        }

        setUploading(false);
        setSelectedFile(null);
        setFolderName('');
        setInputKey(Math.random().toString(36));
    };

    const UploadForm = () => (
        <UploadFormStyle>
          <div>{info}</div>
            {
                uploaded && <a href={blobUrl} />
            }
            <div>
                <Form>
                    <div>
                        <FileTypeButton selected={fileType === 'Document'} value='Document' onClick={handleTabChange}>
                            Documents
                        </FileTypeButton>
                        <FileTypeButton selected={fileType === 'Image'} value='Image' onClick={handleTabChange}>
                            Images
                        </FileTypeButton>
                    </div>
                    {
                        folderName !== 'slides' && (   
                            <StackText>
                                <label htmlFor='firm-name'>
                                    Firm name
                                </label>
                                <input
                                    onChange={handleFirmName}
                                    id='firm-name'
                                    type='text'  
                                    value={firmName}/>
                                {fileNameReminder}
                            </StackText>
                        )
                    }
                    {
                        fileType === 'Image' &&  (
                            <StackText>
                                <label htmlFor={`${fileType}-name`}>
                                    Image type
                                </label>
                                <select 
                                    onChange={handleFolderName}
                                    id={`${fileType}-name`}
                                    type='text' 
                                    name={fileType} 
                                    value={folderName}>
                                        <option>logos</option>
                                        <option>backgrounds</option>
                                        <option>slides</option>
                                </select>
                            </StackText>
                        )
                    }
                    <StackText>
                        <input
                            type='file'
                            data-testid='file-upload'
                            onChange={handleFileChange}
                            key={inputKey || ''} />
                        <span>Relative path: </span>
                        <span>{message}</span>
                        <UploadButton onClick={handleUpload}>
                            Upload File to Azure Storage
                        </UploadButton>
                    </StackText>
                </Form>
            </div>
        </UploadFormStyle>
    );

    return (
        // Something interesting to revisit: rendering <UploadForm /> caused the text input to lose
        // focus after every letter. Changing it to UploadForm() works as expected.
        <Style>
           {storageConfigured && !uploading && UploadForm()}
           {storageConfigured && uploading && <div>Uploading...</div>}
           {!storageConfigured && <div>Unable to verify either storage account or storage upload token.</div>}
        </Style>
    );
};

export default UploadTab;
